"use client";

import Image from "next/image";
import { ScrollParallax } from "react-just-parallax";
import Button from "@/components/inputs/Button";
import { urlFor } from "@/sanity/lib/image";
import type { ExpandedSanityImage } from "@/sanity/lib/types";
import { spacing, typography } from "@/styles/design-tokens";

interface ButtonType {
  _key: string;
  _type: "button";
  text?: string;
  url?: string;
  style?: "primary" | "secondary";
}

interface HeroProps {
  backgroundImage?: ExpandedSanityImage;
  mainImage?: ExpandedSanityImage;
  preText?: string;
  subtext?: string;
  buttons?: ButtonType[];
}

export default function HomeHero({
  backgroundImage,
  mainImage,
  preText,
  subtext,
  buttons,
}: HeroProps) {
  return (
    <section
      className={`${spacing.section} min-h-[100svh] border-b-4 !pt-32 flex items-center overflow-hidden lg:!pt-28`}
    >
      <div className={`${spacing.container} !z-20`}>
        <div className="flex flex-col items-center justify-center space-y-4">
          {preText && (
            <p
              className={`${typography.caption} bg-white border-2 rounded-full py-3 px-6 text-center`}
            >
              {preText}
            </p>
          )}
          {mainImage && (
            <Image
              src={urlFor(mainImage).url()}
              alt={mainImage.alt || "background image"}
              width={840}
              height={520}
              priority
            />
          )}
          {subtext && (
            <p
              className={`${typography.caption} bg-white border-2 rounded-full py-3 px-6 text-center`}
            >
              {subtext}
            </p>
          )}
        </div>
        {Array.isArray(buttons) && buttons.length > 0 && (
          <div className="flex flex-col gap-4 items-center justify-center w-full md:flex-row md:flex-wrap">
            {buttons.map((btn) => (
              <Button key={btn._key} {...btn} />
            ))}
          </div>
        )}
      </div>
      <div className="absolute -top-5 left-1/2 z-10 pointer-events-none">
        <ScrollParallax>
          <Image
            src="/images/sushi-roll.gif"
            alt=""
            width={60}
            height={60}
            unoptimized
          />
        </ScrollParallax>
      </div>
      <div className="absolute top-5 left-1/5 z-10 pointer-events-none hidden sm:block">
        <ScrollParallax>
          <Image
            src="/images/person-3.gif"
            alt=""
            width={120}
            height={120}
            unoptimized
          />
        </ScrollParallax>
      </div>
      <div className="absolute bottom-36 left-1/5 z-10 pointer-events-none hidden sm:block">
        <ScrollParallax>
          <Image
            src="/images/person-5.gif"
            alt=""
            width={80}
            height={80}
            unoptimized
          />
        </ScrollParallax>
      </div>
      <div className="absolute bottom-36 right-1/5 z-10 pointer-events-none hidden sm:block">
        <ScrollParallax>
          <Image
            src="/images/person-2.gif"
            alt=""
            width={80}
            height={80}
            unoptimized
          />
        </ScrollParallax>
      </div>
      <div className="absolute top-20 right-1/5 z-10 pointer-events-none hidden sm:block">
        <ScrollParallax>
          <Image
            src="/images/person-1.gif"
            alt=""
            width={80}
            height={80}
            unoptimized
          />
        </ScrollParallax>
      </div>
      <div className="absolute top-1/3 left-5 z-10 pointer-events-none hidden sm:block">
        <ScrollParallax>
          <Image
            src="/images/person-4.gif"
            alt=""
            width={80}
            height={80}
            unoptimized
          />
        </ScrollParallax>
      </div>
      <div className="absolute top-3/12 left-2/12 z-10 pointer-events-none hidden sm:block">
        <ScrollParallax>
          <Image
            src="/images/star.gif"
            alt=""
            width={80}
            height={80}
            unoptimized
          />
        </ScrollParallax>
      </div>
      <div className="absolute top-3/12 right-2/12 z-10 pointer-events-none hidden sm:block">
        <ScrollParallax>
          <Image
            src="/images/speaker.gif"
            alt=""
            width={80}
            height={80}
            unoptimized
          />
        </ScrollParallax>
      </div>
      <div className="absolute bottom-4/12 right-2/12 z-10 pointer-events-none hidden sm:block">
        <ScrollParallax>
          <Image
            src="/images/candy.gif"
            alt=""
            width={100}
            height={100}
            unoptimized
          />
        </ScrollParallax>
      </div>
      <div className="absolute bottom-3/12 left-2/12 z-10 pointer-events-none hidden sm:block">
        <ScrollParallax>
          <Image
            src="/images/ticket.gif"
            alt=""
            width={80}
            height={80}
            unoptimized
          />
        </ScrollParallax>
      </div>
      <div className="absolute top-1/5 left-20 z-10 pointer-events-none hidden sm:block">
        <ScrollParallax>
          <Image
            src="/images/chopsticks.gif"
            alt=""
            width={120}
            height={40}
            unoptimized
          />
        </ScrollParallax>
      </div>
      <div className="absolute top-2/12 right-20 z-10 pointer-events-none hidden sm:block">
        <ScrollParallax>
          <Image
            src="/images/bandana.gif"
            alt=""
            width={80}
            height={40}
            unoptimized
          />
        </ScrollParallax>
      </div>
      <div className="absolute left-2 top-6/12 sm:top-3/5 sm:left-20 z-10 pointer-events-none">
        <ScrollParallax>
          <Image
            src="/images/hat.gif"
            alt=""
            width={120}
            height={40}
            unoptimized
          />
        </ScrollParallax>
      </div>
      <div className="absolute top-4/12 right-5 z-10 pointer-events-none hidden sm:block">
        <ScrollParallax>
          <Image
            src="/images/record.gif"
            alt=""
            width={80}
            height={80}
            unoptimized
          />
        </ScrollParallax>
      </div>
      <div className="absolute top-3/5 right-20 z-10 pointer-events-none hidden sm:block">
        <ScrollParallax>
          <Image
            src="/images/cookie.gif"
            alt=""
            width={80}
            height={80}
            unoptimized
          />
        </ScrollParallax>
      </div>
      <div className="absolute bottom-1/12 left-1 sm:left-5 sm:bottom-5 z-10 pointer-events-none">
        <ScrollParallax>
          <Image
            src="/images/stage.gif"
            alt=""
            width={80}
            height={80}
            unoptimized
          />
        </ScrollParallax>
      </div>
      <div className="absolute bottom-5 right-2 sm:right-10 sm:bottom-10 z-10 pointer-events-none">
        <ScrollParallax>
          <Image
            src="/images/star.gif"
            alt=""
            width={48}
            height={48}
            unoptimized
          />
        </ScrollParallax>
      </div>
      {backgroundImage && (
        <Image
          src={urlFor(backgroundImage).url()}
          alt={backgroundImage.alt || "background image"}
          fill
          priority
          className="object-cover"
        />
      )}
    </section>
  );
}
